import React,{useState,useEffect} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useAdmin} from '../contexts/AdminContext';

const {FiUsers,FiSend,FiFileText,FiActivity,FiBox}=FiIcons;

const AdminDashboardPage=()=> {
  const [stats,setStats]=useState({
    totalUsers: 0,
    totalCampaigns: 0,
    totalBroadcasts: 0,
  });
  const [isLoading,setIsLoading]=useState(true);
  const {getAdminStats}=useAdmin();

  useEffect(()=> {
    const fetchStats=async ()=> {
      setIsLoading(true);
      try {
        const data=await getAdminStats();
        setStats({
          totalUsers: data.total_users || 0,
          totalCampaigns: data.total_campaigns || 0,
          totalBroadcasts: data.total_broadcasts || 0,
        });
      } catch (error) {
        console.error('Failed to fetch stats:',error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  },[getAdminStats]);

  const statCards=[
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: FiUsers,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Total Campaigns',
      value: stats.totalCampaigns,
      icon: FiBox,
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Total Broadcasts',
      value: stats.totalBroadcasts,
      icon: FiSend,
      color: 'from-green-500 to-green-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
        >
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Manage users and send email broadcasts
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {statCards.map((stat,index)=> (
              <motion.div
                key={stat.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.5,delay: index * 0.1}}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center">
                  <div
                    className={`flex-shrink-0 p-3 bg-gradient-to-r ${stat.color} rounded-lg`}
                  >
                    <SafeIcon icon={stat.icon} className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiActivity} className="h-5 w-5 mr-2" /> Quick
              Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link
                to="/admin/users"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow group"
              >
                <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                  <SafeIcon icon={FiUsers} className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Manage Users
                  </h3>
                  <p className="text-sm text-gray-600">View all active users</p>
                </div>
              </Link>
              <Link
                to="/admin/broadcast"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow group"
              >
                <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                  <SafeIcon icon={FiSend} className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Send Broadcast
                  </h3>
                  <p className="text-sm text-gray-600">Email all users</p>
                </div>
              </Link>
              <Link
                to="/admin/logs"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow group"
              >
                <div className="flex-shrink-0 p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                  <SafeIcon
                    icon={FiFileText}
                    className="h-6 w-6 text-purple-600"
                  />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">View Logs</h3>
                  <p className="text-sm text-gray-600">Broadcast history</p>
                </div>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;